import { useState, useEffect, useRef } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import { getCache, setCache } from '@/utils/cache';

export interface DashboardStats {
  employee_count: number;
  metrics: {
    active_markets?: number;
    rollout_status?: string;
  };
  announcement?: {
    title?: string;
    body?: string;
  };
}

export const useDashboardStats = (): { data: DashboardStats | null; loading: boolean; error: string | null } => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  useEffect(() => {
    if (fetchedRef.current) return;
    let isMounted = true;
    const cacheKey = 'dashboardStats_v1';
    const cached = getCache<DashboardStats>(cacheKey);
    if (cached) {
      if (isMounted) {
        setStats(cached);
        setError(null);
        setLoading(false);
        fetchedRef.current = true;
      }
      return;
    }
    const fetchStats = async () => {
      const currentAccounts = accountsRef.current;
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        if (isMounted) {
          setError('REACT_APP_API_URL environment variable is not set');
          setLoading(false);
        }
        return;
      }
      const endpoint = `${apiUrl}/Home-Page`;
      try {
        let token: string | null = null;
        if (isAuthenticated && currentAccounts.length > 0) {
          token = await acquireApiToken(instance, currentAccounts[0]);
        }
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch(endpoint, { method: 'GET', headers });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.details || data.error || `HTTP ${response.status}`);
        }
        if (data.success && data.data) {
          if (isMounted) {
            setStats(data.data);
            setError(null);
            try { setCache('dashboardStats_v1', data.data, 300); } catch {}
            fetchedRef.current = true;
          }
          return;
        }
        throw new Error(data.error || 'Failed to fetch stats');
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message ?? 'Unknown error');
          setStats(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchStats();
    return () => { isMounted = false; };
  }, [isAuthenticated, instance]);
  return { data: stats, loading, error };
};
